<template>
  <section class="financial__ledger" aria-labelledby="financial-ledger-title">
    <h2 id="financial-ledger-title" tabindex="-1">Shipments</h2>
    <section v-if="withoutShipment.length" class="financial__orphans" aria-labelledby="financial-orphans-title">
      <details>
        <summary>
          <mdicon name="alert-outline" :size="18" aria-hidden="true" />
          <span id="financial-orphans-title"><strong>{{ withoutShipment.length }} carrier {{ withoutShipment.length === 1 ? 'cost has' : 'costs have' }} no known shipment</strong> · {{ orphanAmount }}</span>
        </summary>
        <p class="financial__help">
          These invoice rows have no PacSort shipment: no or several shipment numbers, or a number PacSort does not know.
          Load the booking period, refresh matches, or exclude them with a reason so the cost stays visible.
        </p>
        <div class="financial__table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Invoice</th>
                <th scope="col">Shipment number</th>
                <th scope="col">Charge</th>
                <th scope="col">Carrier cost</th>
                <th scope="col"><span class="visually-hidden">Action</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in orphanPage" :key="`${line.batch.id}:${line.row.id}`">
                <td>{{ line.batch.carrier }} {{ invoiceLabel(line) }}<small>Row {{ line.row.rowNumber }} · {{ line.row.date }}</small></td>
                <td>{{ line.row.trackingNumber || 'No shipment number' }}</td>
                <td>{{ line.row.description }}</td>
                <td class="financial__numeric">{{ amount(cost(line), line.batch.currency) }}</td>
                <td>
                  <button
                    class="financial__button financial__button--small"
                    type="button"
                    :disabled="busy"
                    :aria-label="`Open row ${line.row.rowNumber} on ${line.batch.carrier} invoice ${invoiceLabel(line)}`"
                    @click="openLine(line.batch.id, line.row.id)"
                  >
                    Open row
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="withoutShipment.length > orphanPage.length" class="financial__field-hint">
          Showing {{ orphanPage.length }} of {{ withoutShipment.length }}. Open the carrier invoice and filter “Missing match” to see all.
        </p>
      </details>
    </section>

    <div class="financial__panel financial__ledger-list">
      <template v-if="entries.length">
        <h3 id="financial-ledger-list-title" class="financial__list-title" tabindex="-1">Shipment list</h3>
        <!-- Counts cover the period, the ticked carriers and the customer: a chip shows exactly what it counts. -->
        <div class="financial__list-tools">
          <div ref="chipRow" class="financial__chips" role="group" aria-label="Show shipments">
            <button
              v-for="chip in chips"
              :key="chip.view"
              class="financial__chip"
              :class="{ 'financial__chip--attention': chip.attention && counts[chip.view] }"
              type="button"
              :aria-pressed="view === chip.view"
              @click="choose(chip.view)"
            >
              <mdicon v-if="chip.attention && counts[chip.view]" name="alert-circle-outline" :size="16" aria-hidden="true" /><span>{{ chip.label }}</span><span class="financial__chip-count">{{ numberLabel(counts[chip.view]) }}</span>
            </button>
          </div>
          <div>
            <label for="financial-ledger-more" class="visually-hidden">More views</label>
            <select id="financial-ledger-more" v-model="more" class="form-select financial__more" :class="{ 'financial__more--active': more }">
              <option value="">More…</option>
              <option v-for="option in moreViews" :key="option.view" :value="option.view">{{ option.label }} · {{ numberLabel(counts[option.view]) }}</option>
            </select>
          </div>
          <div>
            <label for="financial-ledger-customer" class="visually-hidden">Customer</label>
            <select id="financial-ledger-customer" v-model="customerId" class="form-select">
              <option value="all">All customers</option>
              <option v-for="customer in customers" :key="customer.id" :value="customer.id">{{ customer.name }}</option>
            </select>
          </div>
          <div class="financial__search">
            <label for="financial-ledger-search" class="visually-hidden">Find a shipment</label>
            <input id="financial-ledger-search" v-model="search" class="form-control" type="search" placeholder="Find a shipment: number, PacSort reference or customer" />
          </div>
        </div>
        <!-- One main line and one small line per row. With one currency it sits in the headers, not on every amount. -->
        <div class="financial__table-wrap" :style="{ '--financial-header-top': `${headerTop}px` }">
          <table>
            <thead>
              <tr>
                <th scope="col">Shipment</th>
                <th scope="col">Status</th>
                <th scope="col">Customer</th>
                <th scope="col">Booked</th>
                <th scope="col" class="financial__numeric">Quoted price (SEK)</th>
                <th scope="col" class="financial__numeric">Actual carrier cost{{ inHeader }}</th>
                <th scope="col" class="financial__numeric">Reviewed customer amount{{ inHeader }}</th>
                <th scope="col" class="financial__numeric">Margin{{ inHeader }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in pageEntries" :key="entry.shipment.itemId" :class="{ 'financial__row--missing': !entry.lines.length }">
                <td>
                  <button
                    class="financial__link financial__ids"
                    type="button"
                    :disabled="busy"
                    :aria-label="`Open shipment ${entry.shipment.trackingNumbers.join(', ')}`"
                    :title="entry.shipment.trackingNumbers.length > 1 ? entry.shipment.trackingNumbers.join(', ') : undefined"
                    @click="open(entry.shipment.itemId)"
                  >
                    {{ entry.shipment.trackingNumbers.join(', ') }}
                  </button>
                  <small class="financial__clip" :class="{ financial__attention: confirmedMissing(entry) }" :title="shipmentNote(entry)">
                    {{ shipmentNote(entry) }}
                  </small>
                </td>
                <td class="financial__status-cell">
                  <span class="financial__badge" :class="badgeClass(entry)"><mdicon :name="statusIcon(entry)" :size="14" aria-hidden="true" /> {{ statusText(entry) }}</span>
                  <small v-if="reasonsOf(entry).length" class="financial__attention financial__clip" :title="reasonsOf(entry).join(' · ')">
                    {{ reasonsOf(entry).join(' · ') }}
                  </small>
                  <small v-else-if="entry.weightDiffers">Weight differs</small>
                </td>
                <td>
                  <span class="financial__clip" :title="entry.shipment.billingName">{{ entry.shipment.billingName }}</span>
                </td>
                <td class="financial__nowrap">
                  {{ localDate(entry.shipment.bookedAt) }}
                  <small>{{ entry.shipment.weightGrams ?? '—' }} g · {{ entry.shipment.service || '—' }}</small>
                </td>
                <td class="financial__numeric financial__nowrap">{{ amount(entry.shipment.bookedPrice, bookedCurrency) }}</td>
                <!-- Costs accumulate across invoices; the quoted shipment price stays separate. -->
                <td class="financial__numeric financial__nowrap">
                  <template v-if="entry.lines.length">
                    <span class="visually-hidden">Cost linked:</span> {{ figure(entry.cost, entryCurrency(entry)) }}
                    <small>
                      <span class="financial__source-name" :title="sourceOf(entry)[0]">{{ sourceOf(entry)[0] }}</span> · {{ sourceOf(entry)[1] }}
                    </small>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="financial__numeric financial__nowrap">
                  {{ entry.price === null ? (entry.lines.length ? 'No price' : '—') : figure(entry.price, entryCurrency(entry)) }}
                  <small v-if="priceNote(entry)">{{ priceNote(entry) }}</small>
                </td>
                <td class="financial__numeric financial__nowrap" :class="{ financial__negative: isNegative(entry.margin) }">
                  {{ entry.margin === null ? '—' : marginLabel(entry) }}
                  <small v-if="percentOf(entry)">{{ percentOf(entry) }}</small>
                </td>
              </tr>
              <tr v-if="!pageEntries.length">
                <td v-if="!periodEntries.length" colspan="8" class="financial__empty">{{ emptyPeriod }}</td>
                <td v-else colspan="8" class="financial__empty">
                  No shipments match these filters.
                  <button class="financial__link" type="button" @click="clearFilters">Clear all filters</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="financial__pagination">
          <span aria-live="polite">{{ numberLabel(filtered.length) }} {{ filtered.length === 1 ? 'shipment' : 'shipments' }} · page {{ page }} of {{ pageCount }}</span>
          <div class="financial__actions">
            <button class="financial__button" type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
            <button class="financial__button" type="button" :disabled="page >= pageCount" @click="page += 1">Next</button>
          </div>
        </div>
      </template>
      <p v-else-if="opening" class="financial__empty" role="status">Opening your workspace…</p>
      <p v-else-if="loading" class="financial__empty" role="status">{{ progress || 'Loading shipments from PacSort…' }}</p>
      <div v-else-if="covering" class="financial__empty">
        <mdicon name="truck-outline" :size="32" aria-hidden="true" />
        <strong>{{ emptyPeriod }}</strong>
      </div>
      <div v-else class="financial__empty">
        <mdicon name="truck-outline" :size="32" aria-hidden="true" />
        <strong>No shipments yet</strong>
        <p>Load a booking period from PacSort, or import a carrier invoice: matched invoice rows add their shipments here automatically.</p>
        <button class="financial__button" type="button" @click="goInvoices">Import a carrier invoice</button>
      </div>
    </div>
  </section>
</template>
<script lang="ts">
import type { FinancialActiveCarrier } from '@/const/Financial'
import type { FinancialLedgerChip, FinancialLedgerView } from '@/types/Components/FinancialLedger'
import type { FinancialShipmentLedgerCustomers } from '@/types/Components/FinancialViewState'
import type {
  FinancialBookingPeriod,
  FinancialLedgerEntry,
  FinancialLedgerFilter,
  FinancialLedgerStatus,
  FinancialLine,
  FinancialLoadedPeriod,
} from '@/types/Models/Financial'
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComputedRef,
  type PropType,
  type Ref,
  type WritableComputedRef,
} from 'vue'
import { FINANCIAL_BOOKED_CURRENCY, FINANCIAL_PAGE_SIZE } from '@/const/Financial'
import { financialCommonCurrency, financialAmountLabel, financialDifferenceLabel, financialPercent, sumFinancialAmounts } from '@/services/FinancialAmounts'
import { financialChargeAmount, financialInvoiceLabel, financialLocalDate } from '@/services/FinancialImport'
import {
  FINANCIAL_LEDGER_STATUS,
  financialLedgerFilter,
  financialLedgerSummary,
  financialShipmentCarriers,
} from '@/services/FinancialLedger'

export default defineComponent({
  name: 'FinancialShipmentLedger',
  props: {
    period: { type: Object as PropType<FinancialBookingPeriod>, required: true },
    entries: { type: Array as PropType<FinancialLedgerEntry[]>, required: true },
    withoutShipment: { type: Array as PropType<FinancialLine[]>, default: () => [] },
    eligible: { type: Function as PropType<(entry: FinancialLedgerEntry) => number>, required: true },
    // Why open rows of a shipment need an individual check (loss, possible double billing, no price).
    reasons: { type: Function as PropType<(entry: FinancialLedgerEntry) => string[]>, default: () => [] },
    busy: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    opening: { type: Boolean, default: false },
    progress: { type: String, default: '' },
    // What the last load from PacSort did, or that it was cancelled.
    result: { type: String, default: '' },
    // Periods loaded from PacSort in this workspace: the facit for what was shipped.
    loadedPeriods: { type: Array as PropType<FinancialLoadedPeriod[]>, default: () => [] },
  },
  emits: ['open', 'open-line', 'go-invoices'],
  setup(props, { emit }) {
    const from: ComputedRef<string> = computed(() => props.period.from)
    const to: ComputedRef<string> = computed(() => props.period.to)
    const selectedCarriers: ComputedRef<FinancialActiveCarrier[]> = computed(() => props.period.carriers)
    const view: Ref<FinancialLedgerView> = ref('all')
    const customerId: Ref<string> = ref('all')
    const search: Ref<string> = ref('')
    const page: Ref<number> = ref(1)
    const statusLabels = FINANCIAL_LEDGER_STATUS
    // Open shipments split by whether every open row can be approved at its PacSort price.
    const needsCheck = (entry: FinancialLedgerEntry): boolean => entry.open > 0 && props.eligible(entry) < entry.open
    const localDate = (value: string): string => financialLocalDate(value) || '—'
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    // The list is always the booking period and the ticked carriers, narrowed to the customer being viewed. The chips
    // count the shipments in it; the chosen view and the search narrow the list.
    const scope: ComputedRef<FinancialLedgerFilter> = computed(() => ({
      from: from.value,
      to: to.value,
      carrier: 'all',
      customerId: customerId.value,
      status: 'all',
      search: '',
    }))
    const inCarriers = (entry: FinancialLedgerEntry): boolean =>
      financialShipmentCarriers(entry.shipment).some((item) => selectedCarriers.value.includes(item))
    const scoped: ComputedRef<FinancialLedgerEntry[]> = computed(() => financialLedgerFilter(props.entries, scope.value).filter(inCarriers))
    // Everything booked in the period with the chosen carriers, whatever the list filters: what the coverage measures.
    const periodEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() =>
      financialLedgerFilter(props.entries, { ...scope.value, customerId: 'all' }).filter(inCarriers),
    )
    // A load lists every shipment booked before the day it ran; bookings on that day or later need a new load.
    const completeBefore = (period: FinancialLoadedPeriod): string => financialLocalDate(period.at)
    const covers = (period: FinancialLoadedPeriod, carriers: FinancialActiveCarrier[]): boolean =>
      period.from <= from.value && period.to >= to.value && carriers.every((item) => period.carriers.includes(item))
    // The latest load from PacSort that covers the chosen period and carriers.
    const covering: ComputedRef<FinancialLoadedPeriod | null> = computed(() => [...props.loadedPeriods].reverse().find((period) => covers(period, selectedCarriers.value)) ?? null)
    // An invoice shipment that a loaded PacSort list should have contained but did not.
    const confirmedMissing = (entry: FinancialLedgerEntry): boolean => {
      if (entry.shipment.source !== 'invoice') return false
      const day = financialLocalDate(entry.shipment.bookedAt)
      const carriers = financialShipmentCarriers(entry.shipment)
      return props.loadedPeriods.some(
        (period) =>
          period.from <= day && day <= period.to && day < completeBefore(period) && carriers.some((item) => period.carriers.includes(item)),
      )
    }
    const emptyPeriod: ComputedRef<string> = computed(() =>
      covering.value
        ? `PacSort shipped nothing booked ${from.value} – ${to.value} with ${selectedCarriers.value.join(', ')}.`
        : 'No shipments are known in this booking period yet. Load it from PacSort to see what was shipped.',
    )
    const amount = financialAmountLabel
    const bookedCurrency = FINANCIAL_BOOKED_CURRENCY
    const entryCurrency = (entry: FinancialLedgerEntry): string =>
      financialCommonCurrency(entry.lines.map((line) => line.batch.currency)) ?? ''
    // A shared header is valid only if every visible amount uses that currency. Otherwise label each row.
    const headerCurrency: ComputedRef<string> = computed(() => financialCommonCurrency(
      pageEntries.value.flatMap((entry) => entry.lines.length ? entry.lines.map((line) => line.batch.currency) : [bookedCurrency]),
    ) ?? '')
    const inHeader: ComputedRef<string> = computed(() => headerCurrency.value ? ` (${headerCurrency.value})` : '')
    const figure = (value: string | null, currency: string): string =>
      financialAmountLabel(value, currency === headerCurrency.value ? '' : currency)
    // Each view is a ledger status filter, narrowed where the page knows more: which open rows need a check, and
    // whether PacSort's list for the shipment's day is loaded.
    const unloaded = (entry: FinancialLedgerEntry): boolean => entry.shipment.source === 'invoice' && !confirmedMissing(entry)
    const linked = (entry: FinancialLedgerEntry): boolean => entry.shipment.source === 'pacsort' && entry.lines.length > 0
    const views: Record<FinancialLedgerView, { status: FinancialLedgerFilter['status']; test?: (entry: FinancialLedgerEntry) => boolean }> = {
      all: { status: 'all' },
      'awaiting-cost': { status: 'awaiting-cost' },
      'to-approve': { status: 'to-approve', test: (entry) => !needsCheck(entry) },
      'to-check': { status: 'to-check', test: needsCheck },
      ready: { status: 'ready' },
      rebill: { status: 'rebill' },
      billed: { status: 'billed' },
      loss: { status: 'loss' },
      invoice: { status: 'all', test: confirmedMissing },
      unloaded: { status: 'all', test: unloaded },
      open: { status: 'open' },
      'no-revenue': { status: 'no-revenue' },
      weight: { status: 'weight' },
      linked: { status: 'all', test: linked },
    }
    const summary: ComputedRef<ReturnType<typeof financialLedgerSummary>> = computed(() => financialLedgerSummary(scoped.value))
    const countOf = (test: (entry: FinancialLedgerEntry) => boolean): number => scoped.value.filter(test).length
    const counts: ComputedRef<Record<FinancialLedgerView, number>> = computed(() => ({
      all: summary.value.shipments,
      'awaiting-cost': summary.value.awaiting,
      'to-approve': countOf((entry) => entry.open > 0 && !needsCheck(entry)),
      'to-check': countOf(needsCheck),
      ready: summary.value.ready,
      rebill: summary.value.rebill,
      billed: summary.value.billed,
      loss: summary.value.loss,
      invoice: countOf(confirmedMissing),
      unloaded: countOf(unloaded),
      open: countOf((entry) => entry.open > 0),
      'no-revenue': summary.value.noRevenue,
      weight: countOf((entry) => entry.weightDiffers),
      linked: countOf(linked),
    }))
    // Attention chips get an icon as well as their colour. The last two only appear while they count something.
    const chipList: FinancialLedgerChip[] = [
      { view: 'all', label: 'All' },
      { view: 'awaiting-cost', label: statusLabels['awaiting-cost'] },
      { view: 'to-approve', label: 'Ready to approve' },
      { view: 'to-check', label: 'Needs a check', attention: true },
      { view: 'ready', label: statusLabels.ready },
      { view: 'rebill', label: statusLabels.rebill, attention: true },
      { view: 'billed', label: statusLabels.billed },
      { view: 'loss', label: 'Cost above the customer price', attention: true },
      { view: 'invoice', label: 'Only on invoices', attention: true, optional: true },
      { view: 'unloaded', label: 'Period not loaded', optional: true },
    ]
    const chips: ComputedRef<FinancialLedgerChip[]> = computed(() => chipList.filter((chip) => !chip.optional || counts.value[chip.view] > 0))
    const moreViews: FinancialLedgerChip[] = [
      { view: 'open', label: 'Open rows · all' },
      { view: 'no-revenue', label: statusLabels['no-revenue'] },
      { view: 'weight', label: 'Weight differs' },
      { view: 'linked', label: 'Carrier cost linked' },
    ]
    // The More select shows its view while one is chosen; choosing a chip puts it back on its placeholder.
    const more: WritableComputedRef<FinancialLedgerView | ''> = computed({
      get: () => (moreViews.some((option) => option.view === view.value) ? view.value : ''),
      set: (value) => {
        view.value = value || 'all'
      },
    })
    const choose = (value: FinancialLedgerView): void => {
      view.value = view.value === value ? 'all' : value
    }
    // Focus that would be lost with the control that held it goes to the All chip, which is then pressed.
    const chipRow: Ref<HTMLElement | null> = ref(null)
    const focusAll = (): void => {
      void nextTick(() => {
        if (document.activeElement === document.body) chipRow.value?.querySelector('button')?.focus()
      })
    }
    const filtered: ComputedRef<FinancialLedgerEntry[]> = computed(() => {
      const rule = views[view.value]
      return financialLedgerFilter(scoped.value, { ...scope.value, status: rule.status, search: search.value })
        .filter(rule.test ?? (() => true))
        .sort((a, b) => b.shipment.bookedAt.localeCompare(a.shipment.bookedAt) || a.shipment.itemId.localeCompare(b.shipment.itemId))
    })
    const pageCount: ComputedRef<number> = computed(() => Math.max(1, Math.ceil(filtered.value.length / FINANCIAL_PAGE_SIZE)))
    const pageEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => filtered.value.slice((page.value - 1) * FINANCIAL_PAGE_SIZE, page.value * FINANCIAL_PAGE_SIZE))
    const customers: ComputedRef<FinancialShipmentLedgerCustomers> = computed(() =>
      [...new Map(props.entries.map((entry) => [entry.shipment.billingId, entry.shipment.billingName]))]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    )
    const orphanPage: ComputedRef<FinancialLine[]> = computed(() => props.withoutShipment.slice(0, 25))
    const cost = (line: FinancialLine): string | null => financialChargeAmount(line.batch, line.row)
    const orphanAmount: ComputedRef<string> = computed(() => {
      const currency = financialCommonCurrency(props.withoutShipment.map((line) => line.batch.currency))
      if (currency === null) return 'Mixed or unconfirmed currencies'
      const costs = props.withoutShipment.map(cost)
      return amount(costs.some((value) => value === null) ? null : sumFinancialAmounts(costs as string[]), currency)
    })
    const invoiceLabel = (line: FinancialLine): string => financialInvoiceLabel(line.batch)
    watch([view, customerId, search, from, to, selectedCarriers], () => {
      page.value = 1
    })
    // A chip that disappears after a load or another customer must not leave the list filtered down to nothing, nor
    // take the focus with it.
    watch(
      () => [counts.value.invoice, counts.value.unloaded],
      ([only, unconfirmed]) => {
        if ((view.value === 'invoice' && !only) || (view.value === 'unloaded' && !unconfirmed)) view.value = 'all'
        if (chipRow.value?.contains(document.activeElement)) focusAll()
      },
    )
    // The list's column headers stick right under the sub-header, which is 68px high from -1px until its sections
    // overflow and a scrollbar makes it taller.
    const headerTop: Ref<number> = ref(67)
    let subHeaderSize: ResizeObserver | null = null
    onMounted(() => {
      const subHeader = document.querySelector<HTMLElement>('.sub-header')
      if (subHeader && typeof ResizeObserver !== 'undefined') {
        subHeaderSize = new ResizeObserver(() => {
          headerTop.value = subHeader.getBoundingClientRect().height + (parseFloat(getComputedStyle(subHeader).top) || 0)
        })
        subHeaderSize.observe(subHeader)
      }
    })
    onBeforeUnmount(() => {
      subHeaderSize?.disconnect()
    })
    watch(pageCount, (count) => {
      if (page.value > count) page.value = count
    })
    const reasonsOf = (entry: FinancialLedgerEntry): string[] => (entry.open ? props.reasons(entry) : [])
    const statusText = (entry: FinancialLedgerEntry): string =>
      entry.status === 'to-review' ? (needsCheck(entry) ? 'Needs a check' : 'Ready to approve') : statusLabels[entry.status]
    const isNegative = (value: string | null): boolean => value !== null && Number(value) < 0
    const marginLabel = (entry: FinancialLedgerEntry): string =>
      financialDifferenceLabel(entry.margin!, entryCurrency(entry) === headerCurrency.value ? '' : entryCurrency(entry))
    const percentOf = (entry: FinancialLedgerEntry): string => {
      const percent = entry.margin === null || entry.cost === null ? null : financialPercent(entry.margin, entry.cost)
      return percent ? `${percent} on cost` : ''
    }
    // Only exceptions get a caption: whether a price is approved already shows in the status badge.
    const priceNote = (entry: FinancialLedgerEntry): string => {
      if (!entry.lines.length) return entry.shipment.bookedPrice === null ? '' : 'Booked in PacSort'
      if (!entry.open && !entry.ready && !entry.billed && !entry.changed) return 'All costs excluded'
      return entry.price === null ? 'Needs a price' : ''
    }
    const badgeClass = (entry: FinancialLedgerEntry): Record<string, boolean> => ({
      'financial__badge--review': needsCheck(entry) || entry.status === 'rebill',
      'financial__badge--ready': ['ready', 'billed'].includes(entry.status),
      'financial__badge--neutral': ['awaiting-cost', 'no-revenue'].includes(entry.status) || (entry.status === 'to-review' && !needsCheck(entry)),
    })
    const statusIcon = (entry: FinancialLedgerEntry): string =>
      ({
        'awaiting-cost': 'clock-outline',
        'to-review': needsCheck(entry) ? 'alert-circle-outline' : 'check-circle-outline',
        ready: 'check-circle-outline',
        billed: 'check-all',
        rebill: 'cash-plus',
        'no-revenue': 'minus-circle-outline',
      })[entry.status as FinancialLedgerStatus]
    // Clearing removes the empty row that holds this button, so focus moves to the All chip.
    const clearFilters = (): void => {
      view.value = 'all'
      customerId.value = 'all'
      search.value = ''
      focusAll()
    }
    // Which invoice file the costs came from, and when it was imported. A long file name gives way, the date does not.
    const sourceOf = (entry: FinancialLedgerEntry): [string, string] => {
      const batches = [...new Map(entry.lines.map((line) => [line.batch.id, line.batch])).values()]
      const latest = batches.reduce((last, batch) => (batch.createdAt > last.createdAt ? batch : last))
      const imported = new Date(latest.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      return batches.length === 1
        ? [financialInvoiceLabel(latest), `imported ${imported}`]
        : [`${batches.length} invoices`, `latest imported ${imported}`]
    }
    const carrierText = (entry: FinancialLedgerEntry): string =>
      [...new Set(Object.values(entry.shipment.trackingCarriers ?? {}))].join(' + ') || entry.shipment.carrier
    // The small line under the shipment number: carrier, PacSort reference and, for a shipment known only from an
    // invoice, whether PacSort's list should have had it.
    const shipmentNote = (entry: FinancialLedgerEntry): string => {
      const known =
        entry.shipment.source !== 'invoice'
          ? ''
          : confirmedMissing(entry)
            ? "Only on an invoice · not in PacSort's list"
            : 'Known from an invoice · period not loaded from PacSort'
      return [carrierText(entry), entry.shipment.itemReference, known].filter(Boolean).join(' · ')
    }
    const open = (id: string): void => {
      emit('open', id)
    }
    const openLine = (importId: string, rowId: string): void => {
      emit('open-line', importId, rowId)
    }
    const goInvoices = (): void => {
      emit('go-invoices')
    }
    return {
      from,
      to,
      selectedCarriers,
      view,
      customerId,
      search,
      periodEntries,
      covering,
      emptyPeriod,
      confirmedMissing,
      localDate,
      clearFilters,
      sourceOf,
      shipmentNote,
      page,
      counts,
      chips,
      chipRow,
      moreViews,
      more,
      choose,
      filtered,
      pageCount,
      pageEntries,
      customers,
      orphanPage,
      orphanAmount,
      entryCurrency,
      bookedCurrency,
      amount,
      inHeader,
      figure,
      headerTop,
      cost,
      numberLabel,
      invoiceLabel,
      reasonsOf,
      statusText,
      isNegative,
      marginLabel,
      percentOf,
      priceNote,
      badgeClass,
      statusIcon,
      open,
      openLine,
      goInvoices,
    }
  },
})
</script>
